/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Gamepad2, Copy, RefreshCw, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";

const Card = ({ title, children, testid, right }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
    <div className="flex items-center justify-between mb-3"><h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">{title}</h3>{right}</div>
    {children}
  </div>
);

const copy = (txt, msg) => navigator.clipboard?.writeText(txt).then(() => toast.success(msg || "Copié")).catch(() => toast.error("Copie impossible"));

export function buildSpec(cle) {
  return `# Spécification d'intégration Recovery-Explorer → Reha-Link (Bombacare) v1.0

Base URL : ${API}/game
Authentification : en-tête HTTP  X-Bombacare-Key: ${cle || "<clé API du kiné>"}
Identifiant patient commun (pseudonymisé) : code "BC-XXXXXX" généré dans Reha-Link (fiche patient → Jeu ↔ Clinique → « Lier à Recovery-Explorer »). Le kiné le saisit dans Recovery-Explorer. Aucune donnée nominative ne transite.

## 1. Vérifier la clé
GET /ping  →  { ok, kine, version }

## 2. Récupérer les réglages imposés par le kiné (à chaque démarrage de séance)
GET /patients/{code}  →  { code, pseudo, cote_atteint, mode_debout_autorise, niveau_max, mode_etude, derniere_seance }
Règles côté jeu : mode assis obligatoire tant que mode_debout_autorise=false ; niveau jouable ≤ niveau_max ; si mode_etude=true, version du jeu figée et réglages non modifiables.

## 3. Envoyer une séance (après chaque partie)
POST /sessions   (201)   body JSON :
{
  "code_patient": "BC-7F3K2A",
  "session_id": "uuid-local-unique",        // idempotence : renvoyer la même séance ne la duplique pas
  "date": "2026-06-10T10:00:00+00:00",     // ISO 8601
  "duree_min": 20.5,
  "niveau": 1..8,
  "outil": "balle_souple | sac_graines | anneau_corde | disque_fibres | galet | maillet_navette | filet_cibles | graines_lumineuses | calebasses | pierres_gue | pirogue | racines | planches | tour_potier | esquives | danse_laterale",
  "membre": "ms" | "mi",                   // membre supérieur / inférieur
  "mode": "assis" | "debout",
  "repetitions": 40,
  "reussite_pct": 0..100,
  "amplitude_deg": 85.0,                   // amplitude estimée (épaule/coude ou hanche/genou)
  "temps_reaction_ms": 900,
  "precision_pct": 0..100,
  "symetrie_pct": 0..100,                  // symétrie gauche/droite
  "douleur": 0..10, "fatigue": 0..10,      // déclarés par le patient
  "intolerance": 0..3,                     // score d'intolérance RV
  "pas": 120, "pertes_equilibre": 1,       // membres inférieurs (optionnels)
  "version_jeu": "1.0.0", "immersif": false
}
Réponse : { id, session_id, statut: "cree" | "deja_recu", hors_regle }

## 4. Hors ligne : file locale puis synchronisation
Stocker chaque séance localement (IndexedDB/localStorage) tant que le réseau est absent, puis :
POST /sessions/batch   body { "sessions": [ ...jusqu'à 200 séances... ] }
Réponse : { resultats:[{session_id, statut, erreur?}], crees, deja_recus, refuses }
Supprimer de la file locale toute séance en statut "cree" ou "deja_recu".

## 5. Exemple JavaScript (à coller dans Recovery-Explorer)
const KEY = "${cle || "<clé>"}"; const BASE = "${API}/game";
const H = { "Content-Type": "application/json", "X-Bombacare-Key": KEY };
export async function reglagesPatient(code) { const r = await fetch(BASE + "/patients/" + code, { headers: H }); if (!r.ok) throw new Error("code inconnu"); return r.json(); }
export async function envoyerSeance(s) {
  const file = JSON.parse(localStorage.getItem("re_outbox") || "[]"); file.push(s); localStorage.setItem("re_outbox", JSON.stringify(file));
  return synchroniser();
}
export async function synchroniser() {
  const file = JSON.parse(localStorage.getItem("re_outbox") || "[]"); if (!file.length || !navigator.onLine) return;
  const r = await fetch(BASE + "/sessions/batch", { method: "POST", headers: H, body: JSON.stringify({ sessions: file }) }); if (!r.ok) return;
  const { resultats } = await r.json(); const ok = new Set(resultats.filter((x) => x.statut !== "refuse").map((x) => x.session_id));
  localStorage.setItem("re_outbox", JSON.stringify(file.filter((s) => !ok.has(s.session_id))));
}
window.addEventListener("online", synchroniser);

Mention obligatoire dans le jeu : « Outil d'accompagnement de la rééducation, ne remplace pas l'avis médical. »
Règle de déblocage (affichage seulement, la décision reste au kiné) : réussite ≥ 80 % sur 2 séances + intolérance ≤ 1 + douleur ≤ 4.`;
}

export default function JeuPage() {
  const [cle, setCle] = useState(null);
  const [show, setShow] = useState(false);
  const [patients, setPatients] = useState(null);
  const [cat, setCat] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/jeu/cle").then(({ data }) => setCle(data)).catch(() => null); api.get("/jeu/patients").then(({ data }) => setPatients(data)).catch(() => null); api.get("/jeu/catalogue").then(({ data }) => setCat(data)).catch(() => null); };
  useEffect(load, []);
  const regen = async () => {
    if (!window.confirm("Régénérer la clé ? Recovery-Explorer devra être reconfiguré avec la nouvelle clé.")) return;
    setBusy(true);
    try { await api.post("/jeu/cle/regenerer"); toast.success("Nouvelle clé générée"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const spec = buildSpec(cle?.cle);
  const masked = cle ? `${cle.cle.slice(0, 9)}••••••••••••${cle.cle.slice(-4)}` : "";
  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-jeu-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#0A1426] text-white grid place-items-center"><Gamepad2 size={20} /></div>
        <div>
          <h1 className="font-[Fraunces] text-2xl md:text-3xl font-bold text-[#0A1426]">Recovery-Explorer</h1>
          <div className="text-[12px] text-[#0A1426]/60">Connexion du jeu thérapeutique en réalité virtuelle · suivi jeu ↔ clinique</div>
        </div>
      </div>
      <div className="text-[12px] text-[#0A1426]/70 bg-[#F5F3EE] rounded-xl px-3 py-2" data-testid="jeu-mention">Outil d'accompagnement de la rééducation, ne remplace pas l'avis médical. Aucune décision automatique : le kiné valide toujours niveaux et mode debout.</div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Clé API (compte kiné unique)" testid="jeu-cle-card" right={<Button data-testid="jeu-cle-regen" variant="outline" size="sm" disabled={busy} onClick={regen} className="h-8 rounded-full text-[12px]"><RefreshCw size={12} className="mr-1" /> Régénérer</Button>}>
          {!cle ? <Loader2 className="animate-spin text-[#2BA3A0]" /> : (
            <>
              <div className="flex items-center gap-2">
                <code data-testid="jeu-cle-value" className="flex-1 text-[13px] bg-[#F5F3EE] rounded-xl px-3 py-2 text-[#0A1426] break-all">{show ? cle.cle : masked}</code>
                <button data-testid="jeu-cle-toggle" onClick={() => setShow(!show)} className="w-10 h-10 rounded-full border border-[#E4E0D5] grid place-items-center text-[#0A1426]">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                <button data-testid="jeu-cle-copy" onClick={() => copy(cle.cle, "Clé copiée")} className="w-10 h-10 rounded-full bg-[#2BA3A0] text-white grid place-items-center"><Copy size={16} /></button>
              </div>
              <div className="text-[12px] text-[#0A1426]/70 mt-2">À saisir une seule fois dans Recovery-Explorer (en-tête <code>X-Bombacare-Key</code>). Point d'entrée : <code>{API}/game</code>. {cle.patients_lies} patient(s) lié(s).</div>
            </>
          )}
        </Card>
        <Card title="Patients liés (identifiant pseudonymisé)" testid="jeu-patients-card">
          {!patients ? <Loader2 className="animate-spin text-[#2BA3A0]" /> : patients.filter((p) => p.code).length === 0 ? <div className="text-[12px] text-[#0A1426]/60">Aucun patient lié. Ouvrez une fiche patient → « Jeu ↔ Clinique » → Lier.</div> : (
            <div className="flex flex-col gap-1.5">
              {patients.filter((p) => p.code).map((p) => (
                <Link key={p.id} to={`/pro/patients/${p.id}/jeu`} data-testid={`jeu-patient-${p.id}`} className="flex items-center justify-between gap-2 py-2 px-3 rounded-xl hover:bg-[#F5F3EE] text-[13px] text-[#0A1426]">
                  <span><b>{p.nom}</b> · <code className="text-[#2BA3A0]">{p.code}</code></span>
                  <span className="text-[12px] text-[#0A1426]/60 flex items-center gap-2">{p.seances} séances · N{p.niveau_max} <ArrowRight size={14} /></span>
                </Link>
              ))}
            </div>
          )}
          {patients && patients.filter((p) => !p.code).length > 0 && <div className="text-[11px] text-[#0A1426]/60 mt-2">{patients.filter((p) => !p.code).length} patient(s) non lié(s).</div>}
        </Card>
      </div>

      {cat && (
        <Card title="Progression des outils par niveau (déblocage validé par le kiné)" testid="jeu-catalogue-card">
          <div className="grid md:grid-cols-2 gap-3">
            {[["ms", "Membres supérieurs"], ["mi", "Membres inférieurs"]].map(([k, l]) => (
              <div key={k}>
                <div className="text-[12px] font-bold text-[#0A1426] mb-1">{l}</div>
                {cat.outils[k].map((o) => (
                  <div key={o.id} className="flex items-start gap-2 py-1 text-[12px] text-[#0A1426] border-b border-[#F5F3EE] last:border-0">
                    <span className="w-6 h-6 rounded-full bg-[#0A1426] text-white text-[10px] font-bold grid place-items-center shrink-0">N{o.niveau}</span>
                    <span><b>{o.nom}</b> <span className="text-[#0A1426]/60">— {o.exigence}</span></span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-[#0A1426]/60 mt-2">{cat.regle_deblocage} Aucun objet n'est une arme ; univers africain respectueux.</div>
        </Card>
      )}

      <Card title="Spécification d'intégration (à coller dans la session Recovery-Explorer)" testid="jeu-spec-card" right={<Button data-testid="jeu-spec-copy" size="sm" onClick={() => copy(spec, "Spécification copiée")} className="h-8 rounded-full bg-[#2BA3A0] text-white text-[12px]"><Copy size={12} className="mr-1" /> Copier</Button>}>
        <pre data-testid="jeu-spec-text" className="text-[11px] leading-relaxed bg-[#0A1426] text-white/90 rounded-xl p-4 overflow-x-auto max-h-[420px] whitespace-pre-wrap">{spec}</pre>
      </Card>
    </div>
  );
}
